import { Reducer } from "redux";
import * as Actions from "./actions";
import { ActionTypes } from "./action-types";
import { generateUniqueNumberId } from "src/utils/uniqueId";

export const defaultInformationFeedKey = "main";

export interface IInformationFeedState {
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    items: Spintr.IInformationFeedItem[];
    params: any;
    categories: any[];
    hasFetchedCategories: boolean;
    importantArticles: Spintr.IInformationFeedItem[];
    hasFetchedImportantArticles: boolean;
    isLoadingImportantArticles: boolean;
}

const initialState: { [key: string]: IInformationFeedState } = {
    [defaultInformationFeedKey]: {
        isLoading: true,
        isLoadingMore: false,
        hasMore: false,
        items: [],
        params: {},
        categories: [],
        hasFetchedCategories: false,
        importantArticles: [],
        hasFetchedImportantArticles: false,
        isLoadingImportantArticles: false
    }
};

const informationFeedReducer: Reducer<{ [key: string]: IInformationFeedState }, Actions.InformationFeedAction> = (state = initialState, action) => {
    const newState = { ...state };

    switch (action.type) {
        case ActionTypes.FetchInformationFeedPending:
            if (action.meta.silent) {
                return state;
            }

            return {
                ...state,
                [action.meta.informationFeedKey]: {
                    ...state[action.meta.informationFeedKey],
                    isLoading: action.meta.params.skip === 0,
                    isLoadingMore: action.meta.params.skip > 0,
                }
            };


        case ActionTypes.FetchInformationFeedFulfilled:
            if (action.meta.isRSS) {
                action.payload.data = (action.payload.data as Spintr.IRssFeedEntry[]).map((item) => ({
                    allowComments: true,
                    commentCount: 0,
                    content: item.text,
                    date: item.date.toString(),
                    id: generateUniqueNumberId(),
                    imageUrl: item.imageUrl,
                    isBlogPost: false,
                    likers: [],
                    priorityLevel: 1,
                    title: item.title,
                    url: item.url,
                    isFavourite: false,
                    isFollowing: false,
                    isRss: true,
                    rssName: item.feed.name
                })) as Spintr.IInformationFeedItem[];
            }

            action.payload.data = action.payload.data as Spintr.IInformationFeedItem[];

            return {
                ...state,
                [action.meta.informationFeedKey]: {
                    ...state[action.meta.informationFeedKey],
                    items: action.meta.params.skip > 0
                        ? [...state[action.meta.informationFeedKey].items, ...action.payload.data]
                        : action.payload.data,
                    params: action.meta.params,
                    hasMore: action.payload.data.length === action.meta.params.take,
                    isLoading: false,
                    isLoadingMore: false,
                }
            };

        case ActionTypes.SetInformationFeedLoading:
            return {
                ...state,
                [action.meta.informationFeedKey]: {
                    ...state[action.meta.informationFeedKey],
                    isLoading: true,
                }
            };

        case ActionTypes.FetchContentCategoriesFulfilled:
            return {
                ...state,
                [defaultInformationFeedKey]: {
                    ...state[defaultInformationFeedKey],
                    hasFetchedCategories: true,
                    categories: action.payload.data.items,
                }
            };
            
        case ActionTypes.FetchImportantArticlesPending:
            return {
                ...state,
                [defaultInformationFeedKey]: {
                    ...state[defaultInformationFeedKey],
                    isLoadingImportantArticles: true,
                }
            };

        case ActionTypes.FetchImportantArticlesRejected:
            return {
                ...state,
                [defaultInformationFeedKey]: {
                    ...state[defaultInformationFeedKey],
                    isLoadingImportantArticles: false,
                }
            };

        case ActionTypes.FetchImportantArticlesFulfilled:
            return {
                ...state,
                [defaultInformationFeedKey]: {
                    ...state[defaultInformationFeedKey],
                    hasFetchedImportantArticles: true,
                    isLoadingImportantArticles: false,
                    importantArticles: action.payload.data,
                }
            };

        case ActionTypes.MarkAsReadPending:
            Object.keys(newState).forEach((key) => {
                newState[key] = {
                    ...newState[key],
                    items: newState[key].items.map((item) => item.id === action.meta.uberId
                        ? { ...item, isUnread: false }
                        : item),
                    importantArticles: newState[key].importantArticles?.filter(x => x.id !== action.meta.uberId) || [],
                };
            });
            return newState;

        case ActionTypes.ClearInformationFeed:
            return {
                ...initialState,
            };

        case ActionTypes.RemoveImportantArticle:
            return {
                ...state,
                [defaultInformationFeedKey]: {
                    ...state[defaultInformationFeedKey],
                    importantArticles: state[defaultInformationFeedKey].importantArticles?.filter(x => x.id !== action.meta.id) || [],
                }
            };

        case ActionTypes.AddImportantArticle:
            return {
                ...state,
                [defaultInformationFeedKey]: {
                    ...state[defaultInformationFeedKey],
                    importantArticles: [action.meta.item, ...state[defaultInformationFeedKey].importantArticles],
                }
            };
            
        case ActionTypes.AddItem:
            return {
                ...state,
                [defaultInformationFeedKey]: {
                    ...state[defaultInformationFeedKey],
                    items: [action.meta.item, ...state[defaultInformationFeedKey].items],
                }
            };

        case ActionTypes.UpdateItem:
            Object.keys(newState).forEach((key) => {
                newState[key] = {
                    ...newState[key],
                    items: newState[key].items.map((item) => item.id === action.meta.item.id
                    ? action.meta.item
                    : item),
                    importantArticles: newState[key].importantArticles?.map((item) => item.id === action.meta.item.id
                    ? action.meta.item
                    : item) || [],
                };
            });

            return newState;

        default:
            return state;
    }
};

export default informationFeedReducer;